
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, CheckCircle, AlertTriangle } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { createStripePayment } from "@/lib/payment";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

interface MembershipData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  address: string;
  city: string;
  postal_code: string;
  membership_type: string;
  amount: number;
  additional_donation: number;
  payment_status: string;
}

export default function AdhesionPaiement() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const membershipId = searchParams.get("id");

  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [membershipData, setMembershipData] = useState<MembershipData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Add more logging to help debug the issue
  useEffect(() => {
    console.log("AdhesionPaiement component mounted", {
      membershipId,
      searchParams: Object.fromEntries(searchParams.entries()),
      currentURL: window.location.href
    });

    const fetchMembershipData = async () => {
      if (!membershipId) {
        console.error("No membership ID found in URL");
        setError("Identifiant d'adhésion manquant");
        setIsLoading(false);
        return;
      }

      try {
        console.log(`Fetching membership data for ID: ${membershipId}`);
        
        // Use RPC function instead of direct SELECT to bypass RLS
        const { data, error } = await supabase
          .rpc('get_membership_for_payment', {
            membership_id: membershipId
          })
          .maybeSingle();

        console.log("Membership query result:", { data, error });

        if (error) {
          throw new Error(`Erreur lors de la récupération des données : ${error.message}`);
        }

        if (!data) {
          throw new Error("Adhésion non trouvée ou déjà payée");
        }

        setMembershipData(data as MembershipData);
      } catch (error) {
        console.error("Error fetching membership data:", error);
        setError(error instanceof Error ? error.message : "Une erreur est survenue");
      } finally {
        setIsLoading(false);
      }
    };

    fetchMembershipData();
  }, [membershipId, searchParams]);

  const handlePayment = async () => {
    if (!membershipData) return;
    
    setIsPaymentProcessing(true);
    console.log("Processing payment for membership:", membershipData);
    
    try {
      const response = await createStripePayment({
        amount: membershipData.amount,
        email: membershipData.email,
        firstName: membershipData.first_name,
        lastName: membershipData.last_name,
        address: membershipData.address,
        city: membershipData.city,
        postalCode: membershipData.postal_code,
        membershipType: membershipData.membership_type as "active" | "benefactor" | "donor",
        additionalDonation: membershipData.additional_donation,
        membershipId: membershipData.id
      });
      
      if (response.sessionUrl) {
        console.log("Payment session created, redirecting to:", response.sessionUrl);
        // Store session ID before redirect
        if (response.sessionId) {
          localStorage.setItem("stripe_session_id", response.sessionId);
        }
        window.location.href = response.sessionUrl;
      } else {
        throw new Error("Impossible de générer le lien de paiement");
      }
    } catch (error) {
      console.error("Erreur lors de la création du paiement:", error);
      toast({
        title: "Erreur de paiement",
        description: "Impossible de procéder au paiement. Veuillez réessayer plus tard.",
        variant: "destructive",
      });
      setIsPaymentProcessing(false);
    }
  };

  // Détermine si l'utilisateur est un donateur ou un adhérent
  const isDonor = membershipData?.membership_type === "donor";
  const actionType = isDonor ? "don" : "adhésion";

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="min-h-screen py-12 pt-36 bg-white">
        <div className="container max-w-xl mx-auto">
          <Card className="border-2">
            <CardContent className="pt-6">
              {isLoading ? (
                <div className="text-center">
                  <Loader2 className="h-10 w-10 text-primary animate-spin mx-auto" />
                  <p className="mt-2">Chargement des informations...</p>
                </div>
              ) : error ? (
                <div className="text-center">
                  <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-2" />
                  <h2 className="text-xl font-bold text-red-500 mb-2">Erreur</h2>
                  <p className="text-gray-600">{error}</p>
                  <p className="text-sm mt-2">Route: {window.location.pathname}, Hash: {window.location.hash}</p>
                  <Button 
                    className="mt-4" 
                    onClick={() => navigate("/adhesion")}
                  >
                    Retour à la page d'adhésion
                  </Button>
                </div>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                    <h1 className="text-2xl font-bold text-dark">Finaliser votre {actionType}</h1>
                    <p className="text-gray-600">Procédez au paiement pour terminer votre {actionType}</p>
                  </div>

                  {membershipData && (
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex justify-between">
                          <h3 className="font-medium">Nom</h3>
                          <p>{membershipData.first_name} {membershipData.last_name}</p>
                        </div>
                        <div className="flex justify-between mt-2">
                          <h3 className="font-medium">Email</h3>
                          <p>{membershipData.email}</p>
                        </div>
                        {!isDonor && (
                          <div className="flex justify-between mt-2">
                            <h3 className="font-medium">Type d'adhésion</h3>
                            <p>{membershipData.membership_type === "active" ? "Membre Actif" : "Membre Bienfaiteur"}</p>
                          </div>
                        )}
                      </div>

                      <Separator />
                      
                      <div className="bg-gray-50 p-4 rounded-lg">
                        {isDonor ? (
                          <div className="flex justify-between mb-2">
                            <h3 className="font-medium">Don</h3>
                            <p>{membershipData.amount}€</p>
                          </div>
                        ) : (
                          <>
                            <div className="flex justify-between mb-2">
                              <h3 className="font-medium">Adhésion</h3>
                              <p>{membershipData.membership_type === "active" ? "50€" : "200€"}</p>
                            </div>
                            {membershipData.additional_donation > 0 && (
                              <div className="flex justify-between mb-2">
                                <h3 className="font-medium">Don supplémentaire</h3>
                                <p>{membershipData.additional_donation}€</p>
                              </div>
                            )}
                          </>
                        )}
                        <Separator className="my-2" />
                        <div className="flex justify-between font-bold">
                          <h3>Total</h3>
                          <p>{membershipData.amount}€</p>
                        </div>
                      </div>

                      <Button 
                        onClick={handlePayment}
                        disabled={isPaymentProcessing}
                        className="w-full py-6 bg-dark hover:bg-dark/90 text-white"
                      >
                        {isPaymentProcessing ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Traitement...
                          </>
                        ) : (
                          <>
                            <CreditCard className="mr-2 h-4 w-4" />
                            Payer {membershipData.amount}€
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
